import React, { useMemo } from 'react';
import { useApp } from '../contexts/AppContext';
import { FileText, TrendingUp, TrendingDown, DollarSign, Download, Printer } from 'lucide-react';
import { exportPnlCSV, printPnlReport } from '../utils/exportUtils';

const formatCurrency = (val) => `$${(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PNL_FIELDS = [
  { key: 'revenue', label: 'Monthly Revenue', isRevenue: true },
  { key: 'cogs', label: 'Cost of Goods Sold (COGS)' },
  { key: 'labor', label: 'Labor' },
  { key: 'rent', label: 'Rent' },
  { key: 'utilities', label: 'Utilities' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'software', label: 'Software' },
  { key: 'insurance', label: 'Insurance' },
];

export default function ReportsPage() {
  const { pnlData, setPnlData, recipes, ingredients } = useApp();

  const update = (key, value) => {
    setPnlData(prev => ({ ...prev, [key]: value }));
  };

  const calculations = useMemo(() => {
    const revenue = Number(pnlData.revenue) || 0;
    const expenses = PNL_FIELDS
      .filter(f => !f.isRevenue)
      .reduce((sum, f) => sum + (Number(pnlData[f.key]) || 0), 0);

    const netProfit = revenue - expenses;
    const profitMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
    const cogsPercent = revenue > 0 ? ((Number(pnlData.cogs) || 0) / revenue) * 100 : 0;
    const laborPercent = revenue > 0 ? ((Number(pnlData.labor) || 0) / revenue) * 100 : 0;

    return { revenue, expenses, netProfit, profitMargin, cogsPercent, laborPercent };
  }, [pnlData]);

  // Expense breakdown for visual
  const expenseBreakdown = PNL_FIELDS
    .filter(f => !f.isRevenue)
    .map(f => ({
      label: f.label,
      value: Number(pnlData[f.key]) || 0,
      percent: calculations.expenses > 0
        ? ((Number(pnlData[f.key]) || 0) / calculations.expenses) * 100
        : 0,
    }))
    .filter(e => e.value > 0)
    .sort((a, b) => b.value - a.value);

  const BAR_COLORS = ['#E23D28', '#FFC107', '#2E7D32', '#2A2A2A', '#E23D28', '#FFC107', '#2E7D32'];

  return (
    <div className="page-enter p-4 md:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E23D28] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="reports-title">
              P&L Snapshot
            </h1>
            <p className="text-sm text-[#2A2A2A]/60">Simple monthly profit and loss overview.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportPnlCSV(pnlData, calculations)} className="pizza-btn-secondary text-sm flex items-center gap-1" data-testid="pnl-export-csv-btn">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => printPnlReport(pnlData, calculations)} className="pizza-btn text-sm flex items-center gap-1" data-testid="pnl-print-btn">
            <Printer className="w-4 h-4" /> Print / PDF
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        {/* Input */}
        <div className="md:col-span-3 pizza-card p-6" data-testid="pnl-form">
          <h2 className="text-2xl text-[#2A2A2A] mb-4" style={{ fontFamily: "'Bebas Neue', cursive" }}>
            Monthly Numbers
          </h2>
          <div className="space-y-3">
            {PNL_FIELDS.map(({ key, label, isRevenue }) => (
              <div key={key} className={`flex items-center gap-3 ${isRevenue ? 'pb-3 mb-3 border-b-2 border-[#F0E6D2]' : ''}`}>
                <label className="w-48 text-sm text-[#2A2A2A]/70 flex-shrink-0">{label}</label>
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2A2A2A]/40 text-sm">$</span>
                  <input
                    type="number" step="0.01" min="0"
                    value={pnlData[key]}
                    onChange={e => update(key, e.target.value)}
                    className={`pizza-input w-full pl-7 ${isRevenue ? 'border-[#2E7D32] bg-[#2E7D32]/5' : ''}`}
                    data-testid={`pnl-${key}-input`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="md:col-span-2 space-y-4">
          {/* Net P&L Card */}
          <div className={`rounded-xl p-6 text-white ${calculations.netProfit >= 0 ? 'bg-[#2E7D32]' : 'bg-[#E23D28]'}`} data-testid="pnl-net-result">
            <div className="flex items-center gap-2 mb-2">
              {calculations.netProfit >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
              <p className="text-xs uppercase text-white/70" style={{ fontFamily: "'Bebas Neue', cursive" }}>Net {calculations.netProfit >= 0 ? 'Profit' : 'Loss'}</p>
            </div>
            <p className="text-4xl font-bold" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="pnl-net-value">
              {formatCurrency(calculations.netProfit)}
            </p>
            <p className="text-sm text-white/70 mt-1">
              {calculations.profitMargin.toFixed(1)}% profit margin
            </p>
          </div>

          {/* Summary Stats */}
          <div className="bg-[#2A2A2A] rounded-xl p-6 text-white" data-testid="pnl-summary">
            <h3 className="text-xl mb-3" style={{ fontFamily: "'Bebas Neue', cursive" }}>Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center border-b border-white/10 pb-2">
                <span className="text-white/60 text-sm">Revenue</span>
                <span className="font-bold text-[#4CAF50]">{formatCurrency(calculations.revenue)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-white/10 pb-2">
                <span className="text-white/60 text-sm">Total Expenses</span>
                <span className="font-bold text-[#E23D28]">{formatCurrency(calculations.expenses)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-white/10 pb-2">
                <span className="text-white/60 text-sm">COGS %</span>
                <span className={`font-bold ${calculations.cogsPercent > 35 ? 'text-[#E23D28]' : 'text-[#4CAF50]'}`}>
                  {calculations.cogsPercent.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/60 text-sm">Labor %</span>
                <span className={`font-bold ${calculations.laborPercent > 35 ? 'text-[#E23D28]' : 'text-[#4CAF50]'}`}>
                  {calculations.laborPercent.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Expense Breakdown */}
          {expenseBreakdown.length > 0 && (
            <div className="pizza-card p-6" data-testid="pnl-expense-breakdown">
              <h3 className="text-xl text-[#2A2A2A] mb-3" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                Expense Breakdown
              </h3>
              <div className="space-y-2">
                {expenseBreakdown.map((exp, i) => (
                  <div key={exp.label}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-[#2A2A2A]/60">{exp.label}</span>
                      <span className="text-xs font-medium text-[#2A2A2A]">{formatCurrency(exp.value)} ({exp.percent.toFixed(0)}%)</span>
                    </div>
                    <div className="h-3 bg-[#F0E6D2] rounded-full overflow-hidden border border-[#2A2A2A]/10">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${exp.percent}%`, background: BAR_COLORS[i % BAR_COLORS.length] }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick stats */}
          <div className="pizza-card p-6">
            <h3 className="text-xl text-[#2A2A2A] mb-3" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Your Data
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="text-center p-3 bg-[#FFF8E7] rounded-lg">
                <p className="text-2xl font-bold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{ingredients.length}</p>
                <p className="text-xs text-[#2A2A2A]/50">Ingredients</p>
              </div>
              <div className="text-center p-3 bg-[#FFF8E7] rounded-lg">
                <p className="text-2xl font-bold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{recipes.length}</p>
                <p className="text-xs text-[#2A2A2A]/50">Recipes</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
